/*
 * Purpose: Branded block node — colored BDNA, Squadbox Mega, THRPY, SensAfffect on Mission map
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { Node } from '@/stores/workflowStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, Wrench, CheckCircle, Trash2, Plus, Target, Play, Bot, Plane, Route } from 'lucide-react';
import { useState, useRef } from 'react';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBrandedBlockDef, getBrandedBlockStyle, isMegaBrandedBlock, brandedBuildStatus } from '@/lib/brandedBlocks';

const NODE_STYLES = {
  start: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/50', icon: Play, iconClass: 'ui-icon text-emerald-600 dark:text-emerald-400' },
  goal: { bg: 'bg-primary/5', border: 'border-primary/40', icon: Target, iconClass: 'ui-icon text-primary' },
  agent: { bg: 'bg-violet-500/10', border: 'border-violet-500/40', icon: Bot, iconClass: 'ui-icon text-violet-600 dark:text-violet-400' },
  drone: { bg: 'bg-sky-500/10', border: 'border-sky-500/40', icon: Plane, iconClass: 'ui-icon text-sky-600 dark:text-sky-400' },
  tube: { bg: 'bg-primary/10', border: 'border-primary/50 border-dashed', icon: Route, iconClass: 'ui-icon text-primary' },
  trigger: { bg: 'bg-muted/40', border: 'border-border', icon: Zap, iconClass: 'ui-icon' },
  tool: { bg: 'bg-muted/40', border: 'border-border', icon: Wrench, iconClass: 'ui-icon' },
  action: { bg: 'bg-muted/40', border: 'border-border', icon: CheckCircle, iconClass: 'ui-icon-ok' },
};

export function WorkflowNode({ node }: { node: Node }) {
  const { selectNode, deleteNode, updateNode, selectedNode } = useWorkflowStore();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [connectMode, setConnectMode] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  const brandedDef = node.type === 'branded' ? getBrandedBlockDef(node.subtype) : undefined;
  const brandedStyle =
    node.type === 'branded'
      ? getBrandedBlockStyle(node.subtype, String(node.config.lockColor ?? 'teal'))
      : null;
  const defaultStyle = NODE_STYLES[node.type as keyof typeof NODE_STYLES] ?? NODE_STYLES.tool;
  const style = brandedStyle ?? defaultStyle;
  const Icon = style.icon;
  const label = getNodeLabel(node);
  const isMega = node.type === 'branded' && isMegaBrandedBlock(node.subtype);
  const buildStatus = node.type === 'branded' ? brandedBuildStatus(node.subtype, node.config) : null;
  const cardWidth = isMega ? 'w-60 sm:w-64' : 'w-48';

  const GRID_SIZE = 10;

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - node.x, y: e.clientY - node.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !nodeRef.current) return;
    const newX = Math.round((e.clientX - dragOffset.x) / GRID_SIZE) * GRID_SIZE;
    const newY = Math.round((e.clientY - dragOffset.y) / GRID_SIZE) * GRID_SIZE;
    updateNode(node.id, { x: Math.max(0, newX), y: Math.max(0, newY) });
  };

  const handleMouseUp = () => setIsDragging(false);

  return (
    <div
      ref={nodeRef}
      data-workflow-node
      style={{ left: `${node.x}px`, top: `${node.y}px` }}
      className="absolute select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <Card
        className={`${cardWidth} p-3 ui-lift-flat ${style.bg} border-2 ${style.border} ${
          selectedNode === node.id ? 'ring-2 ring-offset-2 ring-primary' : ''
        } ${isDragging ? 'opacity-75' : ''} cursor-move relative overflow-hidden`}
        onClick={(e) => {
          e.stopPropagation();
          selectNode(node.id);
        }}
        onMouseDown={handleMouseDown}
      >
        {brandedStyle?.stripe && (
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${brandedStyle.stripe}`} aria-hidden />
        )}

        {brandedDef && (
          <div className="absolute top-1.5 right-8 flex gap-1">
            <Badge className={`text-[9px] px-1.5 py-0 h-4 border-0 ${brandedDef.style.badgeClass}`}>
              {brandedDef.brand}
            </Badge>
            {buildStatus === 'idea' && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-dashed">
                Idea
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-start gap-2">
          <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${style.iconClass}`} />
          <div className="flex-1 min-w-0 pr-6">
            <p className="font-medium text-sm text-foreground truncate">{label}</p>
            {brandedDef && (
              <p className="text-[10px] text-muted-foreground truncate">{brandedDef.shortLabel}</p>
            )}
            {node.config.name && String(node.config.name) !== label && (
              <p className="text-xs text-muted-foreground truncate">{String(node.config.name)}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 absolute top-2 right-1"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(node.id);
            }}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>

        {isMega && (
          <p className="text-[10px] text-muted-foreground mt-2 pl-0.5 line-clamp-2">
            Mega block — squad orchestration via contextual configuration
          </p>
        )}

        <div className="flex gap-1 mt-2 pt-2 border-t border-border/30">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 flex-1"
            title="Connect to next node"
            onClick={(e) => {
              e.stopPropagation();
              setConnectMode(!connectMode);
            }}
          >
            <Plus className={`w-3 h-3 ${connectMode ? 'text-primary' : 'text-muted-foreground'}`} />
          </Button>
        </div>
      </Card>
    </div>
  );
}
